import { ApiMethodSignature, ApiModelTypeField, ApiRunnerMethodType, ApiModelTypeFieldDataType } from 'types/swagger/clarifai-api';
import { ModelSnippetFn } from '../types';

const methodTypes = Object.values(ApiRunnerMethodType);
const apiModelTypeFieldDataTypes = Object.values(ApiModelTypeFieldDataType);

const getMethodTypesByIndex = (index?: ApiRunnerMethodType) => {
  if (!index || typeof index !== 'number' || index < 0 || index >= methodTypes.length) {
    throw new Error('Index out of bounds');
  }
  return methodTypes[index];
};

const getApiModelTypeFieldDataTypesByIndex = (index?: ApiModelTypeFieldDataType) => {
  if (!index || typeof index !== 'number' || index < 0) {
    throw new Error('Index out of bounds');
  }
  if (index >= apiModelTypeFieldDataTypes.length) {
    return 'Any';
  }
  return apiModelTypeFieldDataTypes[index];
};

export const MCP_TRANSPORT_NAME = 'mcp_transport';
export const OPENAI_TRANSPORT_NAME = 'openai_transport';

export const hasMethodSignatureName = ({ name, methodSignatures }: { name: string; methodSignatures?: ApiMethodSignature[] }): boolean => {
  return methodSignatures?.some((methodSignature) => methodSignature.name === name) ?? false;
};

export const generatePythonSDKForLLMWithMethodSignatures: ModelSnippetFn = ({ method_signatures, userId, appId, modelId, deploymentId, pat }) => {
  const clientTemplate = `\
from clarifai.runners.utils import data_types
import os
from clarifai.client import Model

model = Model(
  url = "https://clarifai.com/${userId}/${appId}/models/${modelId}"${
    pat
      ? `,
  pat = "${pat}"`
      : ''
  }${
    deploymentId
      ? `,
  deployment_id = "${deploymentId}", # Only needed for dedicated deployed models`
      : ''
  }
)
`;
  const methodSignaturesStr: string[] = [];
  method_signatures?.forEach((method_signature) => {
    const methodName = method_signature.name;
    if (methodName === 'openai_stream_transport' || methodName === 'openai_transport') {
      return;
    }
    if (
      getMethodTypesByIndex(method_signature.method_type) === ApiRunnerMethodType.UNARY_UNARY ||
      getMethodTypesByIndex(method_signature.method_type) === ApiRunnerMethodType.UNARY_STREAMING
    ) {
      let clientScriptStr = `response = model.${methodName}(`;
      const annotations = getAnnotationsSource(method_signature);
      Object.entries(annotations).forEach(([paramName, [paramType, defaultValue]]) => {
        if (paramName === 'return') return;
        // TODO: disable complex arguments due to issue with method_signatures
        if (paramType.startsWith('List') || paramType.startsWith('data_types') || paramName === 'tool_choice') {
          return;
        }
        if (defaultValue === '""' || defaultValue === '[]' || defaultValue === 'None') {
          defaultValue = setDefaultValue(paramType, paramName);
        }
        defaultValue = typeof defaultValue !== 'object' && Number.isNaN(Number.parseFloat(defaultValue)) ? `"${defaultValue}"` : defaultValue;
        clientScriptStr += `\n    ${paramName} = ${defaultValue}, `;
      });
      clientScriptStr = `${clientScriptStr.slice(0, -2)}\n)`;
      if (getMethodTypesByIndex(method_signature.method_type) === ApiRunnerMethodType.UNARY_UNARY) {
        clientScriptStr += '\nprint(response)';
      } else if (getMethodTypesByIndex(method_signature.method_type) === ApiRunnerMethodType.UNARY_STREAMING) {
        clientScriptStr += '\nfor res in response:\n    print(res)';
      }
      clientScriptStr += '\n';
      methodSignaturesStr.push(clientScriptStr);
    }
  });
  const methodSignaturesStrJoined = methodSignaturesStr.join('\n');
  const script = [
    '# Clarifai Model Client Script',
    '# Set the environment variables `CLARIFAI_DEPLOYMENT_ID` and `CLARIFAI_PAT` to run this script.',
    '# Example usage:',
    clientTemplate,
  ];
  if (methodSignaturesStrJoined) {
    script.push('# Example model prediction from different model methods: \n', methodSignaturesStrJoined, '');
  }
  return script.join('\n');
};

const getAnnotationsSource = (methodSignature: ApiMethodSignature) => {
  const annotations: Record<string, [string, string | null]> = {};
  methodSignature.input_fields?.forEach((input_field) => {
    const paramName = input_field.name;
    if (!paramName) {
      throw new Error(`Field name of ${input_field.name} is undefined`);
    }
    let paramType = getBaseType(input_field);
    if (input_field.iterator) {
      paramType = `Iterator[${paramType}]`;
    }
    let defaultValue: string | null = 'None';
    if (input_field.default) {
      defaultValue = parseDefaultValue(input_field);
    }
    annotations[paramName] = [paramType, defaultValue];
  });
  if (!methodSignature?.output_fields?.length) {
    throw new Error('MethodSignature must have at least one output field');
  }
  methodSignature.output_fields.forEach((output_field) => {
    const paramName = output_field.name;
    if (!paramName) {
      throw new Error(`Field name of ${output_field.name} is undefined`);
    }
    let paramType = getBaseType(output_field);
    if (output_field.iterator) {
      paramType = `Iterator[${paramType}]`;
    }
    annotations[paramName] = [paramType, null];
  });
  return annotations;
};

const getBaseType = (field: ApiModelTypeField): string => {
  const dataType = getApiModelTypeFieldDataTypesByIndex(field.type);
  if (!dataType) {
    throw new Error(`Field type of ${field.name} is undefined`);
  }
  if (dataType === ApiModelTypeFieldDataType.NAMED_FIELDS) {
    const annotations: Record<string, string> = {};
    field.type_args?.forEach((type_arg) => {
      const fieldName = type_arg?.name ?? '';
      const fieldType = getBaseType(type_arg);
      annotations[fieldName] = fieldType;
    });
    return `NamedFields[${Object.entries(annotations)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ')}]`;
  } else if (dataType === ApiModelTypeFieldDataType.TUPLE) {
    const typeArgsStr = field.type_args?.map((ta) => getBaseType(ta)).join(', ');
    return `Tuple[${typeArgsStr}]`;
  } else if (dataType === ApiModelTypeFieldDataType.LIST) {
    if (field?.type_args?.length !== 1) {
      throw new Error('List type must have exactly one type argument');
    }
    const elementType = getBaseType(field.type_args[0]);
    return `List[${elementType}]`;
  } else {
    const typeMap: Record<string, string> = {
      STR: 'str',
      BYTES: 'bytes',
      INT: 'int',
      FLOAT: 'float',
      BOOL: 'bool',
      NDARRAY: 'np.ndarray',
      JSON_DATA: 'data_types.JSON',
      TEXT: 'data_types.Text',
      IMAGE: 'data_types.Image',
      CONCEPT: 'data_types.Concept',
      REGION: 'data_types.Region',
      FRAME: 'data_types.Frame',
      AUDIO: 'data_types.Audio',
      VIDEO: 'data_types.Video',
    };
    return typeMap[dataType] || 'Any';
  }
};

const parseDefaultValue = (field: ApiModelTypeField): string | null => {
  if (!field.default) {
    return null;
  }
  let defaultStr = field.default;
  if (defaultStr === 'null') {
    defaultStr = 'None';
  }
  /**
   * Empty strings with "" need to be re-declared as empty string variable in javascript
   */
  if (defaultStr === '""') defaultStr = '';
  const dataType = getApiModelTypeFieldDataTypesByIndex(field.type);
  if (!dataType) {
    throw new Error(`Field type of ${field.name} is undefined`);
  }
  try {
    if (dataType === ApiModelTypeFieldDataType.INT) {
      return String(Number.parseInt(defaultStr, 10));
    } else if (dataType === ApiModelTypeFieldDataType.FLOAT) {
      return String(Number.parseFloat(defaultStr));
    } else if (dataType === ApiModelTypeFieldDataType.BOOL) {
      return defaultStr.toLowerCase() === 'true' ? 'True' : 'False';
    } else if (dataType === ApiModelTypeFieldDataType.STR) {
      if (defaultStr === '') {
        return '""';
      }
      return defaultStr;
    } else if (dataType === ApiModelTypeFieldDataType.BYTES) {
      return `b${JSON.stringify(defaultStr)}`;
    } else if (dataType === ApiModelTypeFieldDataType.JSON_DATA) {
      const parsed = JSON.parse(defaultStr);
      return JSON.stringify(parsed);
    } else if (
      [ApiModelTypeFieldDataType.LIST, ApiModelTypeFieldDataType.TUPLE, ApiModelTypeFieldDataType.NAMED_FIELDS].includes(
        dataType as ApiModelTypeFieldDataType,
      )
    ) {
      const parsed = JSON.parse(defaultStr);
      return JSON.stringify(parsed);
    } else {
      return defaultStr;
    }
  } catch {
    return defaultStr;
  }
};

const setDefaultValue = (fieldType: string, fieldName?: string) => {
  let defaultValue: string | null = null;
  defaultValue = mapDefaultValue(fieldType, fieldName);
  if (fieldType.startsWith('List[')) {
    const elementType = fieldType.slice(5, -1);
    const elementTypeDefaultValue = mapDefaultValue(elementType, fieldName);
    if (elementTypeDefaultValue !== null) {
      defaultValue = `[${elementTypeDefaultValue}]`;
    }
  } else if (fieldType.startsWith('Tuple[')) {
    const elementTypes = fieldType.slice(6, -1).split(', ');
    const elementTypeDefaults = elementTypes.map((et) => mapDefaultValue(et, fieldName));
    defaultValue = `(${elementTypeDefaults.join(', ')})`;
  } else if (fieldType.startsWith('NamedFields[')) {
    const elementTypes = fieldType.slice(12, -1).split(', ');
    const elementTypeDefaults = elementTypes.map((et) => mapDefaultValue(et, fieldName));
    defaultValue = `{${elementTypeDefaults.join(', ')}}`;
  }
  return defaultValue;
};

const mapDefaultValue = (fieldType: string, fieldName?: string) => {
  let defaultValue: string = 'None';
  if (fieldType === 'str') {
    if (fieldName === 'prompt') {
      defaultValue = 'What is the future of AI?';
    } else if (fieldName === 'system_prompt') {
      defaultValue = 'You are a helpful assistant.';
    } else if (fieldName === 'tool_choice') {
      defaultValue = 'None';
    } else {
      defaultValue = ''; // Default to empty string for other string fields
    }
  } else if (fieldType === 'bytes') {
    defaultValue = '';
  } else if (fieldType === 'int') {
    defaultValue = '0';
  } else if (fieldType === 'float') {
    defaultValue = '0.0';
  } else if (fieldType === 'bool') {
    defaultValue = 'False';
  } else if (fieldType === 'data_types.Image') {
    defaultValue = 'data_types.Image.from_url("https://samples.clarifai.com/metro-north.jpg")';
  } else if (fieldType === 'data_types.Text') {
    defaultValue = 'data_types.Text("What\'s the future of AI?")';
  } else if (fieldType === 'data_types.Audio') {
    defaultValue = 'data_types.Audio.from_url("https://samples.clarifai.com/audio.mp3")';
  } else if (fieldType === 'data_types.Video') {
    defaultValue = 'data_types.Video.from_url("https://samples.clarifai.com/video.mp4")';
  } else if (fieldType === 'data_types.Concept') {
    defaultValue = 'data_types.Concept(id= "concept_id", name="dog", value=0.95)';
  } else if (fieldType === 'data_types.Region') {
    defaultValue = 'data_types.Region(box=[0.1, 0.1, 0.5, 0.5])';
  } else if (fieldType === 'data_types.Frame') {
    defaultValue = 'data_types.Frame.from_url("https://samples.clarifai.com/video.mp4", 0)';
  } else if (fieldType === 'data_types.NDArray') {
    defaultValue = 'data_types.NDArray([1, 2, 3])';
  } else if (fieldType === 'data_types.JSON') {
    defaultValue = '';
  }
  return defaultValue;
};
